/**
 * Automatic Picacg re-login.
 *
 * Picacg API tokens expire after a few days. When the user opted into
 * "Remember login on this device", the username/password live in a
 * device-only encrypted store (`src/db/sourceLoginSecrets.ts`) — never in
 * the synced `SourceAuth` blob. {@link createPicacgRelogin} wraps source
 * methods so that:
 *
 * - an `AuthExpired` on a token-bearing auth blob triggers one re-login
 *   (shared by concurrent callers) followed by a single retry;
 * - a token whose JWT `exp` is already past is renewed before the call, and
 *   one expiring within {@link RENEW_WINDOW_MS} is renewed in the background;
 * - refreshed tokens are applied to later calls in memory and handed to
 *   `onRefreshed` listeners so the app persists them (and syncs them).
 */

import { AuthExpired } from '../../lib/errors.ts';
import type { SourceAuth } from '../types.ts';

/** Renew in the background when the token expires within this window. */
export const RENEW_WINDOW_MS = 24 * 60 * 60_000;
/** Don't retry a failed re-login for the same token more often than this. */
const FAILURE_COOLDOWN_MS = 60_000;

export interface PicacgSavedLogin {
  username: string;
  password: string;
}

export interface PicacgReloginDeps {
  /** Device-saved credentials, or `undefined` when the user didn't opt in. */
  loadSavedLogin: () => Promise<PicacgSavedLogin | undefined>;
  /** Signs in and returns the new API token. */
  login: (saved: PicacgSavedLogin) => Promise<string>;
  now?: () => number;
}

class NoSavedLogin extends Error {}

export function readPicacgToken(auth: SourceAuth | undefined): string | undefined {
  const sourceId = auth?.extra?.veneraPluginSource;
  if (sourceId !== undefined && sourceId !== 'picacg') return undefined;
  const store = auth?.extra?.veneraPlugin;
  if (!store || typeof store !== 'object' || Array.isArray(store)) return undefined;
  const token = (store as Record<string, unknown>).token;
  return typeof token === 'string' && token.trim() ? token : undefined;
}

function withToken(auth: SourceAuth, token: string): SourceAuth {
  const store = auth.extra?.veneraPlugin;
  const base = store && typeof store === 'object' && !Array.isArray(store) ? store : {};
  return {
    ...auth,
    extra: {
      ...auth.extra,
      veneraPlugin: { ...(base as Record<string, unknown>), token },
      veneraPluginSource: 'picacg',
    },
  };
}

/** JWT `exp` in milliseconds, or `undefined` when the token isn't a JWT. */
export function tokenExpiresAt(token: string): number | undefined {
  const payload = token.split('.')[1];
  if (!payload) return undefined;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))) as {
      exp?: unknown;
    };
    return typeof json.exp === 'number' ? json.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

export function createPicacgRelogin(deps: PicacgReloginDeps) {
  const now = deps.now ?? (() => Date.now());
  const refreshed = new Map<string, string>();
  const inFlight = new Map<string, Promise<string>>();
  const failedAt = new Map<string, number>();
  const listeners = new Set<(auth: SourceAuth) => void>();

  function onRefreshed(listener: (auth: SourceAuth) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function currentToken(token: string): string {
    let next = token;
    for (let hops = 0; hops < 8 && refreshed.has(next); hops++) next = refreshed.get(next)!;
    return next;
  }

  /** `auth` with any in-memory refreshed token applied. */
  function apply(auth: SourceAuth | undefined): SourceAuth | undefined {
    const token = readPicacgToken(auth);
    if (!auth || !token) return auth;
    const next = currentToken(token);
    return next === token ? auth : withToken(auth, next);
  }

  /** The auth blob to persist when a refresh replaced the saved token. */
  function reconcile(auth: SourceAuth): SourceAuth | undefined {
    const next = apply(auth);
    return next && next !== auth ? next : undefined;
  }

  function refresh(auth: SourceAuth, staleToken: string): Promise<string> {
    const pending = inFlight.get(staleToken);
    if (pending) return pending;
    const promise = (async () => {
      const saved = await deps.loadSavedLogin();
      if (!saved) throw new NoSavedLogin();
      const last = failedAt.get(staleToken);
      if (last !== undefined && now() - last < FAILURE_COOLDOWN_MS) {
        throw new AuthExpired('Picacg login expired and automatic re-login recently failed.', {
          source: 'picacg',
        });
      }
      let token: string;
      try {
        token = await deps.login(saved);
      } catch (err) {
        failedAt.set(staleToken, now());
        const reason = err instanceof Error ? err.message : String(err);
        throw new AuthExpired(`Picacg login expired and automatic re-login failed: ${reason}`, {
          source: 'picacg',
          cause: err,
        });
      }
      refreshed.set(staleToken, token);
      failedAt.delete(staleToken);
      const healed = withToken(auth, token);
      for (const listener of listeners) listener(healed);
      return token;
    })().finally(() => inFlight.delete(staleToken));
    inFlight.set(staleToken, promise);
    return promise;
  }

  /** Renew a token that is expired (awaited) or about to expire (background). */
  async function renewIfExpiring(auth: SourceAuth | undefined): Promise<SourceAuth | undefined> {
    const token = readPicacgToken(auth);
    if (!auth || !token) return auth;
    const expiresAt = tokenExpiresAt(token);
    if (expiresAt === undefined || expiresAt - now() > RENEW_WINDOW_MS) return auth;
    const renewal = refresh(auth, token);
    if (expiresAt > now()) {
      renewal.catch(() => undefined);
      return auth;
    }
    try {
      return withToken(auth, await renewal);
    } catch {
      // Fall through with the old token; the 401 path reports the failure.
      return auth;
    }
  }

  /** Wrap `fn` so an expired login re-logs in once and retries. */
  function wrap<A extends unknown[], R>(
    fn: (...args: A) => Promise<R>,
    authIndex: number,
  ): (...args: A) => Promise<R> {
    return async (...args: A) => {
      const effective = await renewIfExpiring(apply(args[authIndex] as SourceAuth | undefined));
      const callArgs = [...args] as A;
      callArgs[authIndex] = effective;
      try {
        return await fn(...callArgs);
      } catch (error) {
        const token = readPicacgToken(effective);
        if (!(error instanceof AuthExpired) || !effective || !token) throw error;
        let next: string;
        try {
          next = await refresh(effective, token);
        } catch (refreshError) {
          if (refreshError instanceof NoSavedLogin) throw error;
          throw refreshError;
        }
        callArgs[authIndex] = withToken(effective, next);
        return fn(...callArgs);
      }
    };
  }

  return { apply, reconcile, wrap, onRefreshed };
}
